import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getAccessToken, ApiError } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { HERO_IMAGES } from '../lib/program-images.js';
import { LogoUploader } from '../components/LogoUploader.js';
import { US_STATES, CA_PROVINCES, validatePostalCode, formatPostalCode } from '../lib/address-validation.js';

type SettingsTab = 'notifications' | 'organization' | 'business' | 'location';

interface OrgData {
  id: string;
  name: string;
  slug: string;
  status: string;
  primaryDomain: string | null;
  logoUrl: string | null;
  heroImageUrl: string | null;
  tagline: string | null;
  settings: Record<string, unknown> | null;
  locations: LocationData[];
}

interface BusinessData {
  id: string;
  legalName: string;
  dbaName: string | null;
  ein: string | null;
  bn: string | null;
  country: string;
  businessStructure: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  billingContactName: string | null;
  isDefault: boolean;
}

interface LocationData {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  timezone: string | null;
  businessHours: Record<string, { open: string; close: string }> | null;
  socialLinks: Record<string, string> | null;
  notificationConfig: NotificationConfig | null;
}

interface NotificationConfig {
  channels_enabled: string[];
  quiet_hours_timezone: string;
  quiet_hours_start: string;
  quiet_hours_end: string;
  notification_emails?: string[];
  notification_phones?: string[];
  instant_connect_phones?: string[];
  sms_template?: string;
  voice_template?: string;
  // Legacy single-value fields (backward compat)
  notification_email?: string;
  notification_phone?: string;
  instant_connect_phone?: string;
}

export function SettingsPage() {
  // Read initial tab from URL search params (e.g., /app/settings?tab=location)
  const initialTab = (() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    if (t && ['notifications', 'organization', 'business', 'location'].includes(t)) return t as SettingsTab;
    return 'notifications';
  })();
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const [orgData, setOrgData] = useState<OrgData | null>(null);
  const [businesses, setBusinesses] = useState<BusinessData[]>([]);
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
        setOrgId(payload.org_id);
      } catch { /* */ }
    }
  }, []);

  const fetchOrg = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const [orgResult, bizResult] = await Promise.all([
        apiFetch<OrgData>(`/orgs/${orgId}`),
        apiFetch<{ businesses: BusinessData[] }>(`/orgs/${orgId}/businesses`).catch(() => ({ businesses: [] })),
      ]);
      setOrgData(orgResult);
      setBusinesses(bizResult.businesses);
    } catch (err) {
      console.error('Failed to fetch org:', err);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  if (isLoading || !orgData) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-32 mb-3" />
            <div className="h-10 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const location = orgData.locations[selectedLocationIdx];
  const TABS: { key: SettingsTab; label: string }[] = [
    { key: 'notifications', label: 'Notifications' },
    { key: 'organization', label: 'Account' },
    { key: 'business', label: 'Business' },
    { key: 'location', label: `Location${orgData.locations.length > 1 ? `s (${orgData.locations.length})` : ''}` },
  ];

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">Settings</h2>

      {/* Location picker (if multiple) */}
      {orgData.locations.length > 1 && (
        <select
          value={selectedLocationIdx}
          onChange={e => setSelectedLocationIdx(Number(e.target.value))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          {orgData.locations.map((loc, i) => (
            <option key={loc.id} value={i}>{loc.name}</option>
          ))}
        </select>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'notifications' && location && (
        <NotificationsTab orgId={orgId!} location={location} toast={toast} onSaved={fetchOrg} />
      )}
      {tab === 'organization' && (
        <OrganizationTab org={orgData} toast={toast} onSaved={fetchOrg} />
      )}
      {tab === 'business' && (
        <BusinessTab orgId={orgId!} businesses={businesses} toast={toast} onSaved={fetchOrg} />
      )}
      {tab === 'location' && (
        <LocationsTab orgId={orgId!} locations={orgData.locations} selectedIdx={selectedLocationIdx} onSelectIdx={setSelectedLocationIdx} toast={toast} onSaved={fetchOrg} />
      )}
    </div>
  );
}

// ===================== NOTIFICATIONS TAB =====================

function NotificationsTab({ orgId, location, toast, onSaved }: {
  orgId: string; location: LocationData;
  toast: (type: 'success' | 'error', msg: string) => void; onSaved: () => void;
}) {
  // Migrate legacy single-value config to arrays on load
  const initConfig = (raw: NotificationConfig | null): NotificationConfig => {
    const c = raw ?? {
      channels_enabled: ['email'],
      quiet_hours_timezone: 'America/Chicago',
      quiet_hours_start: '20:00',
      quiet_hours_end: '08:00',
    };
    return {
      ...c,
      notification_emails: c.notification_emails?.length
        ? c.notification_emails
        : c.notification_email ? [c.notification_email] : [],
      notification_phones: c.notification_phones?.length
        ? c.notification_phones
        : c.notification_phone ? [c.notification_phone] : [],
      instant_connect_phones: c.instant_connect_phones?.length
        ? c.instant_connect_phones
        : c.instant_connect_phone ? [c.instant_connect_phone] : [],
    };
  };

  const [config, setConfig] = useState<NotificationConfig>(() => initConfig(location.notificationConfig));
  const [isSaving, setIsSaving] = useState(false);

  const toggleChannel = (ch: string) => {
    const enabled = config.channels_enabled.includes(ch)
      ? config.channels_enabled.filter(c => c !== ch)
      : [...config.channels_enabled, ch];
    setConfig({ ...config, channels_enabled: enabled });
  };

  // Helpers for multi-value fields
  const updateListItem = (field: 'notification_emails' | 'notification_phones' | 'instant_connect_phones', idx: number, value: string) => {
    const list = [...(config[field] ?? [])];
    list[idx] = value;
    setConfig({ ...config, [field]: list });
  };

  const removeListItem = (field: 'notification_emails' | 'notification_phones' | 'instant_connect_phones', idx: number) => {
    const list = (config[field] ?? []).filter((_: string, i: number) => i !== idx);
    setConfig({ ...config, [field]: list });
  };

  const addListItem = (field: 'notification_emails' | 'notification_phones' | 'instant_connect_phones') => {
    const list = [...(config[field] ?? []), ''];
    setConfig({ ...config, [field]: list });
  };

  const save = async () => {
    setIsSaving(true);
    try {
      // Filter out empty strings before saving
      const cleaned = {
        ...config,
        notification_emails: (config.notification_emails ?? []).filter(e => e.trim()),
        notification_phones: (config.notification_phones ?? []).filter(p => p.trim()),
        instant_connect_phones: (config.instant_connect_phones ?? []).filter(p => p.trim()),
      };
      await apiFetch(`/orgs/${orgId}/locations/${location.id}/notification-config`, {
        method: 'PUT', body: JSON.stringify(cleaned),
      });
      toast('success', 'Notification settings saved');
      onSaved();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  const channels = [
    { key: 'email', label: 'Email', desc: 'Receive an email with lead details' },
    { key: 'sms', label: 'SMS Text', desc: 'Get a text with the lead info' },
    { key: 'voice', label: 'Phone Call', desc: 'Press 1 to connect with the lead live' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Channels</h3>
        {channels.map(ch => (
          <div key={ch.key} className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">{ch.label}</p>
              <p className="text-xs text-gray-500">{ch.desc}</p>
            </div>
            <button onClick={() => toggleChannel(ch.key)}
              className={`relative w-11 h-6 rounded-full transition-colors ${config.channels_enabled.includes(ch.key) ? 'bg-primary-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.channels_enabled.includes(ch.key) ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-700">Recipients</h3>

        {/* Notification Emails */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Notification Emails</label>
          {(config.notification_emails ?? []).map((email, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input type="email" value={email} onChange={e => updateListItem('notification_emails', idx, e.target.value)}
                placeholder="owner@studio.com"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
              <button onClick={() => removeListItem('notification_emails', idx)}
                className="text-gray-400 hover:text-red-500 text-sm px-2 py-2" title="Remove">
                &times;
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('notification_emails')}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            + Add email
          </button>
        </div>

        {/* SMS Phones */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SMS Phones</label>
          {(config.notification_phones ?? []).map((phone, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input type="tel" value={phone} onChange={e => updateListItem('notification_phones', idx, e.target.value)}
                placeholder="+1 555 123 4567"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
              <button onClick={() => removeListItem('notification_phones', idx)}
                className="text-gray-400 hover:text-red-500 text-sm px-2 py-2" title="Remove">
                &times;
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('notification_phones')}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            + Add phone
          </button>
        </div>

        {/* Instant Connect Phones */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Instant Connect Phones</label>
          {(config.instant_connect_phones ?? []).map((phone, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <input type="tel" value={phone} onChange={e => updateListItem('instant_connect_phones', idx, e.target.value)}
                placeholder="+1 555 123 4567"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
              <button onClick={() => removeListItem('instant_connect_phones', idx)}
                className="text-gray-400 hover:text-red-500 text-sm px-2 py-2" title="Remove">
                &times;
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('instant_connect_phones')}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            + Add phone
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Quiet Hours</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start" value={config.quiet_hours_start} onChange={v => setConfig({ ...config, quiet_hours_start: v })} type="time" />
          <Field label="End" value={config.quiet_hours_end} onChange={v => setConfig({ ...config, quiet_hours_end: v })} type="time" />
        </div>
        <p className="text-xs text-gray-400">Email is not affected by quiet hours.</p>
      </div>

      {/* Message Templates */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-700">Message Templates</h3>
        <p className="text-xs text-gray-400">Customize the SMS and voice messages sent when a new lead comes in. Use merge fields to include lead details.</p>

        {/* Merge field buttons */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs text-gray-400">Insert:</span>
          {[
            { tag: '{{lead_name}}', label: 'Lead Name' },
            { tag: '{{lead_date}}', label: 'Time Received' },
          ].map(f => (
            <button key={f.tag} type="button"
              onClick={() => {
                // Copy tag to clipboard for pasting
                navigator.clipboard?.writeText(f.tag);
              }}
              className="text-xs px-2 py-0.5 bg-primary-50 text-primary-700 rounded font-mono hover:bg-primary-100 transition-colors"
              title={`Click to copy ${f.tag}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* SMS Template */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SMS Message</label>
          <textarea
            value={config.sms_template ?? 'New lead: {{lead_name}} is interested in your studio! Received {{lead_date}}. Log in to follow up.'}
            onChange={e => setConfig({ ...config, sms_template: e.target.value })}
            rows={3}
            maxLength={320}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-400 mt-1">{(config.sms_template ?? '').length}/320 characters (SMS limit)</p>
        </div>

        {/* Voice Template */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Voice Message (read aloud by phone)</label>
          <textarea
            value={config.voice_template ?? 'You have a new lead. {{lead_name}} is interested in your studio. They reached out {{lead_date}}. Press 1 to connect with them now.'}
            onChange={e => setConfig({ ...config, voice_template: e.target.value })}
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-400 mt-1">This text is spoken by the automated voice call system.</p>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-500 mb-1">Preview (sample lead)</p>
          <p className="text-xs text-gray-700">
            <span className="font-medium">SMS:</span>{' '}
            {(config.sms_template ?? 'New lead: {{lead_name}} is interested in your studio! Received {{lead_date}}. Log in to follow up.')
              .replace(/\{\{lead_name\}\}/g, 'Sarah Johnson')
              .replace(/\{\{lead_date\}\}/g, 'Mar 30, 2:15 PM')}
          </p>
          <p className="text-xs text-gray-700 mt-2">
            <span className="font-medium">Voice:</span>{' '}
            {(config.voice_template ?? 'You have a new lead. {{lead_name}} is interested in your studio. They reached out {{lead_date}}. Press 1 to connect with them now.')
              .replace(/\{\{lead_name\}\}/g, 'Sarah Johnson')
              .replace(/\{\{lead_date\}\}/g, 'Mar 30, 2:15 PM')}
          </p>
        </div>
      </div>

      <SaveButton onClick={save} isSaving={isSaving} />
    </div>
  );
}

// ===================== ORGANIZATION TAB =====================

function OrganizationTab({ org, toast, onSaved }: {
  org: OrgData; toast: (type: 'success' | 'error', msg: string) => void; onSaved: () => void;
}) {
  const [name, setName] = useState(org.name);
  const [domain, setDomain] = useState(org.primaryDomain ?? '');
  const [logoUrl, setLogoUrl] = useState(org.logoUrl ?? '');
  const [heroImageUrl, setHeroImageUrl] = useState(org.heroImageUrl ?? '');
  const [tagline, setTagline] = useState(org.tagline ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const heroOptions = HERO_IMAGES;

  const save = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/orgs/${org.id}`, {
        method: 'PUT', body: JSON.stringify({
          name,
          primary_domain: domain || undefined,
          logo_url: logoUrl || undefined,
          hero_image_url: heroImageUrl || undefined,
          tagline: tagline || undefined,
        }),
      });
      toast('success', 'Account updated');
      onSaved();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Account Details</h3>
        <Field label="Account Name" value={name} onChange={setName} placeholder="Downtown Martial Arts" />
        <Field label="Website" value={domain} onChange={setDomain} placeholder="www.downtownma.com" />
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600">Status</label>
          <span className={`inline-flex text-xs font-medium px-2 py-1 rounded-full ${
            org.status === 'live' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>{org.status.replace(/_/g, ' ')}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Branding</h3>
        <LogoUploader
          currentUrl={logoUrl}
          onSave={async (dataUrl) => {
            setLogoUrl(dataUrl);
            // Auto-save immediately after crop
            try {
              await apiFetch(`/orgs/${org.id}`, {
                method: 'PUT',
                body: JSON.stringify({ logo_url: dataUrl }),
              });
              toast('success', 'Logo saved');
              onSaved();
            } catch {
              toast('error', 'Failed to save logo');
            }
          }}
        />
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Hero Image</label>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {heroOptions.map(opt => (
              <button
                key={opt.url}
                type="button"
                onClick={() => setHeroImageUrl(opt.url)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all aspect-[8/3] ${
                  heroImageUrl === opt.url ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img src={opt.url} alt={opt.label} className="w-full h-full object-cover" />
                {heroImageUrl === opt.url && (
                  <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">Use custom URL instead</summary>
            <input
              type="url"
              value={heroImageUrl.startsWith('/images/') ? '' : heroImageUrl}
              onChange={e => setHeroImageUrl(e.target.value)}
              placeholder="https://example.com/hero.jpg"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2"
            />
          </details>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tagline</label>
          <input
            type="text"
            value={tagline}
            onChange={e => setTagline(e.target.value.slice(0, 300))}
            placeholder="Your studio tagline (max 300 characters)"
            maxLength={300}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <p className="text-[10px] text-gray-400 mt-1 text-right">{tagline.length}/300</p>
        </div>
      </div>

      <SaveButton onClick={save} isSaving={isSaving} />
    </div>
  );
}

// ===================== LOCATION TAB =====================

function LocationTab({ orgId, location, toast, onSaved }: {
  orgId: string; location: LocationData;
  toast: (type: 'success' | 'error', msg: string) => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: location.name,
    address_line1: location.addressLine1 ?? '',
    address_line2: location.addressLine2 ?? '',
    city: location.city ?? '',
    state: location.state ?? '',
    zip: location.zip ?? '',
    phone: location.phone ?? '',
    email: location.email ?? '',
    timezone: location.timezone ?? 'America/Chicago',
  });
  const [hours, setHours] = useState<Record<string, { open: string; close: string }>>(
    location.businessHours ?? {}
  );
  const [social, setSocial] = useState<Record<string, string>>(
    location.socialLinks ?? {}
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const SOCIALS = ['facebook', 'instagram', 'google', 'yelp', 'tiktok'];

  const save = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/orgs/${orgId}/locations/${location.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name,
          addressLine1: form.address_line1 || null,
          addressLine2: form.address_line2 || null,
          city: form.city || null,
          state: form.state || null,
          zip: form.zip || null,
          phone: form.phone || null,
          email: form.email || null,
          timezone: form.timezone,
          businessHours: Object.keys(hours).length > 0 ? hours : null,
          socialLinks: Object.keys(social).filter(k => social[k]).length > 0
            ? Object.fromEntries(Object.entries(social).filter(([, v]) => v))
            : null,
        }),
      });
      toast('success', 'Location updated');
      onSaved();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Location Details</h3>
        <Field label="Location Name" value={form.name} onChange={v => updateField('name', v)} placeholder="Main Studio" />
        <Field label="Address" value={form.address_line1} onChange={v => updateField('address_line1', v)} placeholder="123 Main St" />
        <Field label="Address Line 2" value={form.address_line2} onChange={v => updateField('address_line2', v)} placeholder="Suite 100" />
        <div className="grid grid-cols-3 gap-2">
          <Field label="City" value={form.city} onChange={v => updateField('city', v)} placeholder="Dallas" />
          <Field label="State" value={form.state} onChange={v => updateField('state', v)} placeholder="TX" />
          <Field label="ZIP" value={form.zip} onChange={v => updateField('zip', v)} placeholder="75201" />
        </div>
        <Field label="Phone" value={form.phone} onChange={v => updateField('phone', v)} type="tel" placeholder="(214) 555-1234" />
        <Field label="Email" value={form.email} onChange={v => updateField('email', v)} type="email" placeholder="info@studio.com" />
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
          <select value={form.timezone} onChange={e => updateField('timezone', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'Pacific/Honolulu'].map(tz => (
              <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Business Hours */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Business Hours</h3>
        {DAYS.map(day => (
          <div key={day} className="flex items-center gap-2">
            <span className="w-20 text-xs text-gray-600 capitalize">{day}</span>
            <input type="time" value={hours[day]?.open ?? ''} onChange={e => setHours(h => ({ ...h, [day]: { ...h[day]!, open: e.target.value, close: h[day]?.close ?? '21:00' } }))}
              className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-xs" />
            <span className="text-xs text-gray-400">to</span>
            <input type="time" value={hours[day]?.close ?? ''} onChange={e => setHours(h => ({ ...h, [day]: { open: h[day]?.open ?? '09:00', close: e.target.value } }))}
              className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-xs" />
            {hours[day] ? (
              <button onClick={() => setHours(h => { const n = { ...h }; delete n[day]; return n; })} className="text-xs text-red-400 hover:text-red-600">Clear</button>
            ) : (
              <button onClick={() => setHours(h => ({ ...h, [day]: { open: '09:00', close: '21:00' } }))} className="text-xs text-primary-600">Set</button>
            )}
          </div>
        ))}
      </div>

      {/* Social Links */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Social Links</h3>
        {SOCIALS.map(platform => (
          <Field key={platform} label={platform.charAt(0).toUpperCase() + platform.slice(1)}
            value={social[platform] ?? ''} onChange={v => setSocial(s => ({ ...s, [platform]: v }))}
            placeholder={`https://${platform}.com/yourstudio`} />
        ))}
      </div>

      <SaveButton onClick={save} isSaving={isSaving} />
    </div>
  );
}

// ===================== SHARED COMPONENTS =====================

function Field({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
    </div>
  );
}

// ===================== BUSINESS TAB =====================

function BusinessTab({ orgId, businesses, toast, onSaved }: {
  orgId: string; businesses: BusinessData[];
  toast: (type: 'success' | 'error', msg: string) => void; onSaved: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});

  const startEdit = (biz: BusinessData) => {
    setEditingId(biz.id);
    setEditForm({
      country: biz.country ?? 'US',
      legal_name: biz.legalName, dba_name: biz.dbaName ?? '', ein: biz.ein ?? '',
      bn: biz.bn ?? '', business_structure: biz.businessStructure ?? '',
      address_line1: biz.addressLine1 ?? '', city: biz.city ?? '', state: biz.state ?? '',
      postal_code: biz.postalCode ?? '', billing_contact_name: biz.billingContactName ?? '',
      billing_email: biz.billingEmail ?? '', billing_phone: biz.billingPhone ?? '',
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      // Clean empty strings to undefined so Zod optional validators don't reject them
      const cleaned: Record<string, string | undefined> = {};
      for (const [k, v] of Object.entries(editForm)) {
        cleaned[k] = v.trim() || undefined;
      }
      // Always include country and derive currency
      cleaned['country'] = editForm['country'] || 'US';
      cleaned['currency'] = editForm['country'] === 'CA' ? 'CAD' : 'USD';
      await apiFetch(`/orgs/${orgId}/businesses/${editingId}`, {
        method: 'PUT', body: JSON.stringify(cleaned),
      });
      toast('success', 'Business updated');
      setEditingId(null);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        toast('error', err.message || 'Validation failed — check your fields');
      } else {
        toast('error', 'Failed to save');
      }
    }
  };

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ legal_name: '', dba_name: '', ein: '', business_structure: '', billing_email: '', billing_phone: '' });

  const handleAdd = async () => {
    if (!addForm.legal_name) return;
    try {
      await apiFetch(`/orgs/${orgId}/businesses`, {
        method: 'POST',
        body: JSON.stringify({ ...addForm, is_default: true }),
      });
      toast('success', 'Business added');
      setShowAdd(false);
      onSaved();
    } catch { toast('error', 'Failed to add business'); }
  };

  if (businesses.length === 0) {
    return (
      <div className="space-y-4">
        {showAdd ? (
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Add Business Details</h3>
            <Field label="Legal Business Name *" value={addForm.legal_name} onChange={v => setAddForm(f => ({ ...f, legal_name: v }))} placeholder="Downtown Martial Arts LLC" />
            <Field label="DBA / Trade Name" value={addForm.dba_name} onChange={v => setAddForm(f => ({ ...f, dba_name: v }))} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="EIN" value={addForm.ein} onChange={v => setAddForm(f => ({ ...f, ein: v }))} placeholder="XX-XXXXXXX" />
              <Field label="Structure" value={addForm.business_structure} onChange={v => setAddForm(f => ({ ...f, business_structure: v }))} placeholder="LLC, Corp, etc." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Billing Email" value={addForm.billing_email} onChange={v => setAddForm(f => ({ ...f, billing_email: v }))} type="email" />
              <Field label="Billing Phone" value={addForm.billing_phone} onChange={v => setAddForm(f => ({ ...f, billing_phone: v }))} type="tel" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={!addForm.legal_name} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">Save</button>
              <button onClick={() => setShowAdd(false)} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
            <p className="text-gray-500 text-sm">No business details yet.</p>
            <button onClick={() => setShowAdd(true)} className="mt-2 text-sm text-primary-600 hover:text-primary-800 font-medium">
              + Add Business Details
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {businesses.map(biz => (
        <div key={biz.id} className="bg-white rounded-xl border border-gray-100 p-4">
          {editingId === biz.id ? (
            <BusinessEditFormInline form={editForm} setForm={setEditForm} country={editForm['country'] ?? biz.country}
              onSave={saveEdit} onCancel={() => setEditingId(null)}
              einError={validateEin(editForm['ein'] ?? '')} />
          ) : (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 text-sm">{biz.legalName}</p>
                    {biz.isDefault && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Default</span>}
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{biz.country}</span>
                  </div>
                  {biz.dbaName && <p className="text-xs text-gray-500 mt-0.5">DBA: {biz.dbaName}</p>}
                </div>
                <button onClick={() => startEdit(biz)} className="text-xs text-primary-600 font-medium">Edit</button>
              </div>
              <div className="text-xs text-gray-400 mt-2 space-y-0.5">
                {biz.ein && <p>EIN: {biz.ein}</p>}
                {biz.bn && <p>BN: {biz.bn}</p>}
                {biz.businessStructure && <p className="capitalize">{biz.businessStructure.replace(/_/g, ' ')}</p>}
                {biz.addressLine1 && <p>{biz.addressLine1}, {biz.city}, {biz.state} {biz.postalCode}</p>}
                {biz.billingEmail && <p>Billing: {biz.billingContactName ? `${biz.billingContactName} — ` : ''}{biz.billingEmail}</p>}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add another business */}
      {showAdd ? (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Add Business</h3>
          <Field label="Legal Business Name *" value={addForm.legal_name} onChange={v => setAddForm(f => ({ ...f, legal_name: v }))} placeholder="Eastside Fitness Inc" />
          <Field label="DBA / Trade Name" value={addForm.dba_name} onChange={v => setAddForm(f => ({ ...f, dba_name: v }))} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="EIN" value={addForm.ein} onChange={v => setAddForm(f => ({ ...f, ein: v }))} placeholder="XX-XXXXXXX" />
            <Field label="Structure" value={addForm.business_structure} onChange={v => setAddForm(f => ({ ...f, business_structure: v }))} placeholder="LLC, Corp, etc." />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Billing Email" value={addForm.billing_email} onChange={v => setAddForm(f => ({ ...f, billing_email: v }))} type="email" />
            <Field label="Billing Phone" value={addForm.billing_phone} onChange={v => setAddForm(f => ({ ...f, billing_phone: v }))} type="tel" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={!addForm.legal_name} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">Save</button>
            <button onClick={() => setShowAdd(false)} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="w-full py-3 text-sm text-primary-600 hover:text-primary-800 font-medium border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 transition-colors">
          + Add Business
        </button>
      )}
    </div>
  );
}

// ===================== LOCATIONS TAB (plural, with add) =====================

function LocationsTab({ orgId, locations, selectedIdx, onSelectIdx, toast, onSaved }: {
  orgId: string; locations: LocationData[]; selectedIdx: number; onSelectIdx: (i: number) => void;
  toast: (type: 'success' | 'error', msg: string) => void; onSaved: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: '', address_line1: '', city: '', state: '', zip: '', phone: '', email: '' });

  const handleAdd = async () => {
    if (!newLoc.name) return;
    try {
      await apiFetch(`/orgs/${orgId}/locations`, {
        method: 'POST',
        body: JSON.stringify(newLoc),
      });
      toast('success', `${newLoc.name} added`);
      setShowAdd(false);
      setNewLoc({ name: '', address_line1: '', city: '', state: '', zip: '', phone: '', email: '' });
      onSaved();
    } catch { toast('error', 'Failed to add location'); }
  };

  return (
    <div className="space-y-4">
      {/* Location list */}
      {locations.map((loc, i) => (
        <button key={loc.id} onClick={() => onSelectIdx(i)}
          className={`w-full bg-white rounded-xl border p-4 text-left transition-all ${selectedIdx === i ? 'border-primary-300 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 text-sm">{loc.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {[loc.addressLine1, loc.city, loc.state].filter(Boolean).join(', ') || 'No address set'}
              </p>
              {loc.phone && <p className="text-xs text-gray-400 mt-0.5">{loc.phone}</p>}
            </div>
            {selectedIdx === i && <span className="text-xs text-primary-600 font-medium">Selected</span>}
          </div>
        </button>
      ))}

      {/* Add location */}
      {showAdd ? (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Add Location</h3>
          <Field label="Location Name *" value={newLoc.name} onChange={v => setNewLoc(l => ({ ...l, name: v }))} placeholder="Eastside Studio" />
          <Field label="Address" value={newLoc.address_line1} onChange={v => setNewLoc(l => ({ ...l, address_line1: v }))} placeholder="456 Oak Ave" />
          <div className="grid grid-cols-3 gap-2">
            <Field label="City" value={newLoc.city} onChange={v => setNewLoc(l => ({ ...l, city: v }))} />
            <Field label="State" value={newLoc.state} onChange={v => setNewLoc(l => ({ ...l, state: v }))} />
            <Field label="ZIP" value={newLoc.zip} onChange={v => setNewLoc(l => ({ ...l, zip: v }))} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Phone" value={newLoc.phone} onChange={v => setNewLoc(l => ({ ...l, phone: v }))} type="tel" />
            <Field label="Email" value={newLoc.email} onChange={v => setNewLoc(l => ({ ...l, email: v }))} type="email" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={!newLoc.name} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">Add</button>
            <button onClick={() => setShowAdd(false)} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="w-full py-3 text-sm text-primary-600 hover:text-primary-800 font-medium border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-300 transition-colors">
          + Add Location
        </button>
      )}

      {/* Edit selected location */}
      {locations[selectedIdx] && (
        <div className="pt-2 border-t border-gray-100">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Edit: {locations[selectedIdx]!.name}</h3>
          <LocationTab orgId={orgId} location={locations[selectedIdx]!} toast={toast} onSaved={onSaved} />
        </div>
      )}
    </div>
  );
}

// ===================== EIN VALIDATION =====================

const STRUCTURES_LIST = [
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'non_profit', label: 'Non-Profit' },
  { value: 'other', label: 'Other' },
];

function validateEin(ein: string): string | null {
  if (!ein) return null;
  const digits = ein.replace(/\D/g, '');
  if (digits.length > 0 && digits.length < 9) return 'EIN must be 9 digits (XX-XXXXXXX)';
  if (digits.length === 9) return null; // valid
  if (digits.length > 9) return 'EIN must be exactly 9 digits';
  return null;
}

function formatEin(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
}

function validateBn(bn: string): string | null {
  if (!bn) return null;
  const digits = bn.replace(/\D/g, '');
  if (digits.length > 0 && digits.length < 9) return 'BN must be 9 digits';
  if (digits.length > 9) return 'BN must be exactly 9 digits';
  return null;
}

function formatBn(raw: string): string {
  return raw.replace(/[^\d]/g, '').slice(0, 9);
}

// ===================== BUSINESS EDIT FORM (inline) =====================

function BusinessEditFormInline({ form, setForm, country, onSave, onCancel, einError }: {
  form: Record<string, string>;
  setForm: (fn: (f: Record<string, string>) => Record<string, string>) => void;
  country: string;
  onSave: () => void;
  onCancel: () => void;
  einError: string | null;
}) {
  const hasEinError = einError !== null;
  const bnError = country === 'CA' ? validateBn(form['bn'] ?? '') : null;
  const hasBnError = bnError !== null;
  const postalError = validatePostalCode(form['postal_code'] ?? '', country);
  const canSave = !hasEinError && !hasBnError && !postalError;

  return (
    <div className="space-y-3">
      {/* Country selector */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, country: 'US' }))}
            className={`flex-1 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
              country === 'US' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            USA
          </button>
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, country: 'CA' }))}
            className={`flex-1 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
              country === 'CA' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            Canada
          </button>
        </div>
      </div>
      <Field label="Legal Business Name" value={form['legal_name'] ?? ''} onChange={v => setForm(f => ({ ...f, legal_name: v }))} />
      <Field label="DBA / Trade Name" value={form['dba_name'] ?? ''} onChange={v => setForm(f => ({ ...f, dba_name: v }))} />
      <div className="grid grid-cols-2 gap-2">
        {country === 'US' ? (
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-xs font-medium text-gray-600">EIN</label>
              <div className="group relative">
                <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-52 shadow-lg">
                    <p className="font-medium">Employer Identification Number</p>
                    <p>Format: XX-XXXXXXX (9 digits)</p>
                  </div>
                </div>
              </div>
            </div>
            <input
              type="text"
              value={form['ein'] ?? ''}
              onChange={e => setForm(f => ({ ...f, ein: formatEin(e.target.value) }))}
              placeholder="XX-XXXXXXX"
              maxLength={10}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                hasEinError ? 'border-red-300 bg-red-50' :
                (form['ein'] ?? '').replace(/\D/g, '').length === 9 ? 'border-green-300 bg-green-50' :
                'border-gray-200'
              }`}
            />
            {hasEinError && <p className="text-xs text-red-500 mt-0.5">{einError}</p>}
            {!hasEinError && (form['ein'] ?? '').replace(/\D/g, '').length === 9 && <p className="text-xs text-green-600 mt-0.5">Valid format</p>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-xs font-medium text-gray-600">Business Number (BN)</label>
              <div className="group relative">
                <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-56 shadow-lg">
                    <p className="font-medium">CRA Business Number</p>
                    <p>Format: 9 digits (123456789)</p>
                    <p>With program: 123456789 RT 0001</p>
                  </div>
                </div>
              </div>
            </div>
            <input
              type="text"
              value={form['bn'] ?? ''}
              onChange={e => setForm(f => ({ ...f, bn: formatBn(e.target.value) }))}
              placeholder="123456789"
              maxLength={15}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                validateBn(form['bn'] ?? '') ? 'border-red-300 bg-red-50' :
                (form['bn'] ?? '').replace(/\D/g, '').length === 9 ? 'border-green-300 bg-green-50' :
                'border-gray-200'
              }`}
            />
            {validateBn(form['bn'] ?? '') && <p className="text-xs text-red-500 mt-0.5">{validateBn(form['bn'] ?? '')}</p>}
            {!validateBn(form['bn'] ?? '') && (form['bn'] ?? '').replace(/\D/g, '').length === 9 && <p className="text-xs text-green-600 mt-0.5">Valid format</p>}
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Structure</label>
          <select
            value={form['business_structure'] ?? ''}
            onChange={e => setForm(f => ({ ...f, business_structure: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select...</option>
            {STRUCTURES_LIST.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <Field label="Address" value={form['address_line1'] ?? ''} onChange={v => setForm(f => ({ ...f, address_line1: v }))} placeholder="Street address" />
      <div className="grid grid-cols-3 gap-2">
        <Field label="City" value={form['city'] ?? ''} onChange={v => setForm(f => ({ ...f, city: v }))} />
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{country === 'US' ? 'State' : 'Province'}</label>
          <select
            value={form['state'] ?? ''}
            onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
            className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              form['state'] && !(country === 'US' ? US_STATES : CA_PROVINCES).some(s => s.code === form['state'])
                ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          >
            <option value="">Select...</option>
            {(country === 'US' ? US_STATES : CA_PROVINCES).map(s => (
              <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
            ))}
          </select>
          {form['state'] && !(country === 'US' ? US_STATES : CA_PROVINCES).some(s => s.code === form['state']) && (
            <p className="text-[10px] text-red-500 mt-0.5">
              &quot;{form['state']}&quot; doesn&apos;t match {country === 'US' ? 'US states' : 'Canadian provinces'}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{country === 'US' ? 'ZIP Code' : 'Postal Code'}</label>
          <input
            type="text"
            value={form['postal_code'] ?? ''}
            onChange={e => setForm(f => ({ ...f, postal_code: formatPostalCode(e.target.value, country) }))}
            placeholder={country === 'US' ? '12345' : 'A1A 1A1'}
            maxLength={country === 'US' ? 10 : 7}
            className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              validatePostalCode(form['postal_code'] ?? '', country)
                ? 'border-red-300 bg-red-50'
                : (form['postal_code'] ?? '').length > 0 && !validatePostalCode(form['postal_code'] ?? '', country)
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-200'
            }`}
          />
          {validatePostalCode(form['postal_code'] ?? '', country) && (
            <p className="text-[10px] text-red-500 mt-0.5">{validatePostalCode(form['postal_code'] ?? '', country)}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Billing Contact" value={form['billing_contact_name'] ?? ''} onChange={v => setForm(f => ({ ...f, billing_contact_name: v }))} />
        <Field label="Billing Email" value={form['billing_email'] ?? ''} onChange={v => setForm(f => ({ ...f, billing_email: v }))} type="email" />
        <Field label="Billing Phone" value={form['billing_phone'] ?? ''} onChange={v => setForm(f => ({ ...f, billing_phone: v }))} type="tel" />
      </div>
      <div className="flex gap-2">
        <button onClick={onSave} disabled={!canSave} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">Save</button>
        <button onClick={onCancel} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

function SaveButton({ onClick, isSaving }: { onClick: () => void; isSaving: boolean }) {
  return (
    <div className="sticky bottom-16 md:bottom-0 bg-gray-50 py-3 -mx-4 px-4">
      <button onClick={onClick} disabled={isSaving}
        className="w-full bg-primary-600 text-white py-3 rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors">
        {isSaving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}
